import React, { useState } from 'react';
import { useStripe } from '../contexts/StripeContext';
import { useAuth } from '../contexts/AuthContext';

function Paywall() {
  const { createSubscriptionCheckout } = useStripe();
  const { currentUser } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showMonthly, setShowMonthly] = useState(false);

  const features = [
    'Unlimited workshop hosting',
    'Calendar integration',
    'Booking management system',
    'Automated email confirmations',
    'Workshop analytics',
    'Custom workshop pages',
    'Payment processing',
    'Priority support'
  ];

  const plans = {
    yearly: {
      id: 'yearly',
      name: 'Annual Plan',
      price: 30,
      period: 'month',
      billedAs: '$360 billed annually',
      savings: '$240',
      description: 'Best value - Save $240 per year',
      popular: true
    },
    monthly: {
      id: 'monthly',
      name: 'Monthly Plan',
      price: 50,
      period: 'month',
      billedAs: '$50 billed monthly',
      description: 'Flexible month-to-month access',
      popular: false
    }
  };

  const handleSubscribe = async () => {
    if (!currentUser) {
      setError('Please log in to subscribe');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await createSubscriptionCheckout(selectedPlan);
      // Redirect happens in createSubscriptionCheckout
    } catch (error) {
      setError(error.message || 'Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  const currentPlan = plans[selectedPlan];

  return (
    <div className="fixed inset-0 z-[9999] bg-klorah-background/95 backdrop-blur-md">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-6">
              <h1 className="text-3xl tracking-wider text-white font-light">
                S E S S I O N S
              </h1>
              <span className="px-2 py-0.5 text-xs bg-klorah-primary/10 rounded text-klorah-primary font-medium border border-klorah-primary/20">
                PRO
              </span>
            </div>
            
            <h2 className="text-2xl font-semibold text-white mb-4">
              Unlock Your Teaching Potential
            </h2>
            
            <p className="text-klorah-text leading-relaxed">
              Join thousands of instructors using Sessions to manage workshops, handle bookings, and grow their teaching business.
            </p>
          </div>

          {/* Pricing Card */}
          <div className="glass-panel p-8 mb-6">
            {/* Plan Selector */}
            <div className="mb-8">
              {/* Annual Plan */}
              <div 
                className={`relative rounded-2xl border-2 p-6 mb-4 cursor-pointer transition-all duration-300 ${
                  selectedPlan === 'yearly' 
                    ? 'border-klorah-primary bg-klorah-primary/5' 
                    : 'border-klorah-border bg-klorah-card hover:border-klorah-primary/50'
                }`}
                onClick={() => setSelectedPlan('yearly')}
              >
                {plans.yearly.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="px-3 py-1 text-xs font-semibold bg-klorah-primary text-black rounded-full">
                      RECOMMENDED
                    </span>
                  </div>
                )}
                
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{plans.yearly.name}</h3>
                    <p className="text-sm text-klorah-text">{plans.yearly.description}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedPlan === 'yearly' 
                      ? 'border-klorah-primary bg-klorah-primary' 
                      : 'border-klorah-border'
                  }`}>
                    {selectedPlan === 'yearly' && (
                      <div className="w-2 h-2 rounded-full bg-black"></div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-baseline space-x-2 mb-2">
                  <span className="text-3xl font-bold text-white">${plans.yearly.price}</span>
                  <span className="text-klorah-text">/{plans.yearly.period}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-klorah-text-light">{plans.yearly.billedAs}</span>
                  <span className="text-sm font-medium text-emerald-400">Save {plans.yearly.savings}/year</span>
                </div>
              </div>

              {/* Show Monthly Toggle */}
              <button
                onClick={() => setShowMonthly(!showMonthly)}
                className="w-full text-sm text-klorah-primary hover:text-klorah-primary/80 transition-colors mb-4"
              >
                {showMonthly ? '↑ Hide monthly option' : '↓ Show monthly option'}
              </button>

              {/* Monthly Plan (conditionally shown) */}
              {showMonthly && (
                <div 
                  className={`rounded-2xl border-2 p-6 cursor-pointer transition-all duration-300 ${
                    selectedPlan === 'monthly' 
                      ? 'border-klorah-primary bg-klorah-primary/5' 
                      : 'border-klorah-border bg-klorah-card hover:border-klorah-primary/50'
                  }`}
                  onClick={() => setSelectedPlan('monthly')}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{plans.monthly.name}</h3>
                      <p className="text-sm text-klorah-text">{plans.monthly.description}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === 'monthly' 
                        ? 'border-klorah-primary bg-klorah-primary' 
                        : 'border-klorah-border'
                    }`}>
                      {selectedPlan === 'monthly' && (
                        <div className="w-2 h-2 rounded-full bg-black"></div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-baseline space-x-2 mb-2">
                    <span className="text-3xl font-bold text-white">${plans.monthly.price}</span>
                    <span className="text-klorah-text">/{plans.monthly.period}</span>
                  </div>
                  
                  <span className="text-sm text-klorah-text-light">{plans.monthly.billedAs}</span>
                </div>
              )}
            </div>

            {/* Features */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-white mb-4">Everything included:</h4>
              <div className="grid grid-cols-1 gap-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-5 h-5 rounded-full bg-klorah-primary/20 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-klorah-primary" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-klorah-text">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="w-full py-4 px-6 bg-klorah-primary text-black font-semibold rounded-xl hover:bg-klorah-primary/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                `Subscribe for ${currentPlan.billedAs}`
              )}
            </button>

            {/* Error Message */}
            {error && (
              <div className="mt-4 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-400 text-sm text-center">
                {error}
              </div>
            )}

            {/* Security Note */}
            <div className="mt-6 text-center">
              <p className="text-xs text-klorah-text-light">
                🔒 Secure payment powered by Stripe • Cancel anytime
              </p>
            </div>
          </div>

          {/* Benefits */}
          <div className="text-center text-sm text-klorah-text-light">
            <p>Join 1,000+ instructors already using Sessions Pro</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Paywall; 